import { FC, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import axios from 'axios'
import { CheckCircle2, AlertCircle, MessageCircle, Loader2, RefreshCcw, Power } from 'lucide-react'
import { PageCard, SectionLabel, TOKENS, ConfirmDialog } from '../../../components/design-system'

const API_URL = process.env.REACT_APP_API_URL

type ConnectResponse = { qr_base64: string | null; state: string; instance_name: string }
type StatusResponse = { state: string; connected: boolean }

async function postConnect(company_id: number): Promise<ConnectResponse> {
  const { data } = await axios.post<{ data: ConnectResponse }>(`${API_URL}/notifications/whatsapp/${company_id}/connect`)
  return data.data
}
async function getStatus(company_id: number): Promise<StatusResponse> {
  const { data } = await axios.get<{ data: StatusResponse }>(`${API_URL}/notifications/whatsapp/${company_id}/status`)
  return data.data
}
async function postDisconnect(company_id: number): Promise<void> {
  await axios.post(`${API_URL}/notifications/whatsapp/${company_id}/disconnect`)
}

const STATE_LABEL: Record<string, string> = {
  open: 'Conectado',
  connecting: 'Conectando…',
  qr: 'Aguardando leitura do QR Code',
  close: 'Desconectado',
  unknown: 'Status desconhecido',
}

export const WhatsAppConnect: FC<{ companyId: number }> = ({ companyId }) => {
  const qc = useQueryClient()
  const [qrPayload, setQrPayload] = useState<ConnectResponse | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  const { data: status, isFetching: pollingStatus } = useQuery(
    ['notifications', 'whatsapp', 'status', companyId],
    () => getStatus(companyId),
    {
      refetchInterval: qrPayload && !qrPayload.qr_base64 ? false : 5_000,
      refetchOnWindowFocus: false,
    },
  )

  const connectMut = useMutation<ConnectResponse, any, void>(
    () => postConnect(companyId),
    {
      onSuccess: (data) => {
        setQrPayload(data)
        if (data.state === 'open') {
          toast.success('WhatsApp já conectado.')
        } else {
          toast.info('Escaneie o QR code com o WhatsApp do celular.')
        }
        qc.invalidateQueries(['notifications', 'whatsapp', 'status', companyId])
      },
      onError: (e: any) => { toast.error(`Erro ao conectar: ${e?.response?.data?.message ?? e.message}`) },
    },
  )

  const disconnectMut = useMutation<void, any, void>(
    () => postDisconnect(companyId),
    {
      onSuccess: () => {
        toast.success('WhatsApp desconectado.')
        setQrPayload(null)
        setConfirmDisconnect(false)
        qc.invalidateQueries(['notifications', 'whatsapp', 'status', companyId])
      },
      onError: (e: any) => { toast.error(`Erro ao desconectar: ${e?.response?.data?.message ?? e.message}`) },
    },
  )

  const effectiveState = status?.state ?? qrPayload?.state ?? 'unknown'
  const connected = status?.connected === true
  const showQr = !connected && qrPayload?.qr_base64

  return (
    <PageCard>
      <SectionLabel>WhatsApp</SectionLabel>
      <div style={{ marginTop: TOKENS.spacing.sm, marginBottom: TOKENS.spacing.md, fontSize: 12, color: TOKENS.color.muted, lineHeight: 1.5 }}>
        Conecte um número de WhatsApp da empresa pra enviar notificações automáticas via WhatsApp.
        A plataforma cuida da infraestrutura — você só precisa escanear o QR code com o celular onde está o WhatsApp da empresa.
      </div>

      {/* Status pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
        padding: '8px 12px', borderRadius: TOKENS.radius.chip,
        background: connected ? TOKENS.color.severity.low.bg
          : effectiveState === 'qr' || effectiveState === 'connecting'
            ? TOKENS.color.actionSoft
            : TOKENS.color.surface,
        color: connected ? TOKENS.color.severity.low.text
          : effectiveState === 'qr' || effectiveState === 'connecting'
            ? TOKENS.color.action
            : TOKENS.color.muted,
        marginBottom: TOKENS.spacing.md,
        width: 'fit-content',
      }}>
        {connected ? <CheckCircle2 size={14} />
          : effectiveState === 'connecting' || effectiveState === 'qr' ? <Loader2 size={14} />
            : <AlertCircle size={14} />}
        {STATE_LABEL[effectiveState] ?? effectiveState}
        {pollingStatus && <span style={{ opacity: 0.6, marginLeft: 4 }}>·</span>}
      </div>

      {/* QR display */}
      {showQr && (
        <div style={{
          background: TOKENS.color.surface,
          border: `1px solid ${TOKENS.color.border}`,
          borderRadius: TOKENS.radius.card,
          padding: TOKENS.spacing.lg,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          marginBottom: TOKENS.spacing.md,
        }}>
          <img
            src={qrPayload!.qr_base64!}
            alt='QR Code do WhatsApp'
            style={{ width: 256, height: 256, imageRendering: 'pixelated', borderRadius: 8, background: '#fff' }}
          />
          <div style={{ marginTop: TOKENS.spacing.md, fontSize: 12, color: TOKENS.color.muted, textAlign: 'center', maxWidth: 320, lineHeight: 1.5 }}>
            No WhatsApp do celular: <strong>Configurações → Aparelhos conectados → Conectar um aparelho</strong>.
            Aponte a câmera pra esse QR code.
          </div>
          <div style={{ marginTop: TOKENS.spacing.sm, fontSize: 11, color: TOKENS.color.muted }}>
            O status atualiza sozinho assim que o pareamento concluir.
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: TOKENS.spacing.sm }}>
        {!connected && (
          <button
            type='button'
            onClick={() => connectMut.mutate()}
            disabled={connectMut.isLoading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', fontSize: 13, fontWeight: 700,
              border: 'none',
              background: connectMut.isLoading ? TOKENS.color.border : TOKENS.color.action,
              color: connectMut.isLoading ? TOKENS.color.muted : '#fff',
              borderRadius: TOKENS.radius.chip,
              cursor: connectMut.isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {connectMut.isLoading
              ? <><Loader2 size={14} /> Gerando QR…</>
              : qrPayload ? <><RefreshCcw size={14} /> Gerar novo QR</>
                : <><MessageCircle size={14} /> Conectar WhatsApp</>}
          </button>
        )}
        {connected && (
          <button
            type='button'
            onClick={() => setConfirmDisconnect(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', fontSize: 13, fontWeight: 700,
              border: `1px solid ${TOKENS.color.severity.high.text}`,
              background: TOKENS.color.card,
              color: TOKENS.color.severity.high.text,
              borderRadius: TOKENS.radius.chip,
              cursor: 'pointer',
            }}
          >
            <Power size={14} /> Desconectar
          </button>
        )}
      </div>

      <ConfirmDialog
        open={confirmDisconnect}
        title='Desconectar WhatsApp?'
        description='A integração WhatsApp ficará offline. As notificações por WhatsApp pararão de ser enviadas até que você reconecte.'
        confirmLabel='Desconectar'
        tone='danger'
        loading={disconnectMut.isLoading}
        onConfirm={() => disconnectMut.mutate()}
        onCancel={() => setConfirmDisconnect(false)}
      />
    </PageCard>
  )
}
